import { FC, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import dayjs, { Dayjs } from "utils/dayjs"

import ConfirmModal from "components/ConfirmModal/ConfirmModal"
import { Grid, GridItem } from "components/Grid"
import FormField from "components/FormField/FormField"
import InputDateTime from "components/InputDateTime/InputDateTime"
import { EVENT_STEP, EVENT_STEP_UNIT } from "config/constants"
import ValidationMessage from "components/ValidationMessage/ValidationMessage"
import { Guid } from "@/types"
import useReservation from "hooks/useReservation"
import Spinner from "components/Spinner/Spinner"
import useEarlyReturnEndDateValidation, { EndDateError } from "hooks/useEarlyReturnEndDateValidation"
import Routes from "routes/index"
import EarlyReturnEndDateErrors from "components/EarlyReturnEndDateErrors/EarlyReturnEndDateErrors"

interface IEarlyReturnModalProps {
    reservationId: Guid
    onClose: () => void
}

const EarlyReturnModal: FC<IEarlyReturnModalProps> = ({
    reservationId,
    onClose,
}) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { reservation } = useReservation({ reservationId, withPricingDetails: true })
    const reservationStart = reservation?.time.reservationStart
    const reservationEnd = reservation?.time.reservationEnd
    const endDateMin = dayjs(reservationStart).add(EVENT_STEP, EVENT_STEP_UNIT)
    const endDateMax = dayjs(reservationEnd).subtract(EVENT_STEP, EVENT_STEP_UNIT)
    const [endDate, setEndDate] = useState<Dayjs | null>(null)
    const [endDateErrors, setEndDateErrors] = useState<EndDateError[]>([])
    const [isSubmitted, setIsSubmitted] = useState(false)
    const [isValidatingEndDate, setIsValidatingEndDate] = useState(false)
    const { validateEndDate } = useEarlyReturnEndDateValidation()

    function handleEndDateChange(date: Dayjs | null) {
        setEndDate(date)
        setEndDateErrors([])
    }

    async function handleSubmit() {
        setIsSubmitted(true)

        if (!endDate) return

        if (!reservation || !reservation.pricingDetails?.invoicePositions) {
            setEndDateErrors([EndDateError.system])
            return
        }

        setIsValidatingEndDate(true)

        try {
            const errors = await validateEndDate({
                reservationId: reservation.id,
                reservationAssets: reservation.assets,
                latestNotCanceledInvoiceEnd: reservation.latestNotCanceledInvoiceEnd,
                endDate,
            })
            setEndDateErrors(errors)

            if (errors.length) {
                setIsValidatingEndDate(false)
                return
            }

            navigate(Routes.earlyReturn.getPath({ reservationId }, { end: endDate.toISOString() }))
        } catch (e) {
            setEndDateErrors([EndDateError.system])
        } finally {
            setIsValidatingEndDate(false)
        }

    }

    return (
        <ConfirmModal
            title={`${t('earlyReturn.earlyReturn')}${reservation?.number ? ` (${reservation.number})` : ''}`}
            maxWidth="586px"
            confirmButtonLabel={t('common.next')}
            onClose={onClose}
            onConfirm={endDateErrors.length ? undefined : handleSubmit}
            isBusy={!reservation || isValidatingEndDate}>
            {reservation
                ? <>
                    {reservation.customer.name}{reservation.customer.number ? ` (${reservation.customer.number})` : ''}
                    <Grid columns="minmax(0, 1fr) minmax(0, 1fr)" gap="2.4rem" className="mt-5">
                        <GridItem>
                            <FormField label={t('common.start')} isText>
                                {reservationStart?.format(t('config.dateLongTimeFormat'))}
                            </FormField>
                        </GridItem>
                        <GridItem>
                            <FormField label={t('common.end')}>
                                <InputDateTime
                                    type="datetime"
                                    date={endDate}
                                    onDateChange={handleEndDateChange}
                                    utc
                                    minutesStep={EVENT_STEP}
                                    defaultHours={23}
                                    defaultMinutes={45}
                                    minDate={endDateMin}
                                    maxDate={endDateMax}
                                    defaultActiveStartDate={endDate ? undefined : (reservationEnd ? dayjs(reservationEnd).set("date", 15).toDate() : undefined)}
                                    isInvalid={isSubmitted && !endDate}
                                    disabled={isValidatingEndDate}
                                />
                            </FormField>
                            {isSubmitted && !endDate &&
                                <ValidationMessage>{t('validation.required')}</ValidationMessage>
                            }
                        </GridItem>
                    </Grid>
                    <EarlyReturnEndDateErrors
                        reservationId={reservationId}
                        errors={endDateErrors}
                        className="mt-3"
                    />
                </>
                : <Spinner className="py-4" />
            }
        </ConfirmModal>
    )
}

export default EarlyReturnModal
